import React, { useMemo, forwardRef } from 'react';
import { FormControl, InputLabel, FormHelperText } from '@mui/material';
import ReactSelect from 'react-select';
import { translate as t } from 'hooks/useTranslations';
import { FieldTypeEnum } from '../_types';

export type FieldTypePickerProps = {
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: string;
  sx?: any;
  allowMultiple?: boolean;
};

const FieldTypePicker = forwardRef<HTMLDivElement, FieldTypePickerProps>(
  (
    {
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled,
      required,
      error,
      helperText,
      size,
      sx,
      allowMultiple = false,
    },
    ref
  ) => {
    const options = useMemo(() => {
      const fieldTypeValues: FieldTypeEnum[] = ['Text', 'Number', 'Boolean', 'Date', 'Time', 'SingleSelect', 'Lookup'];
      return fieldTypeValues.map((type) => ({
        value: type,
        label: t(`performanceReporting.fieldBank.fieldType.${type}`),
      }));
    }, []);

    // Single-select: value is the selected option object or null
    // Multi-select: value is an array of selected option objects
    const selectedValue = useMemo(() => {
      if (allowMultiple) {
        return options.filter((x) => (value ?? []).includes(x.value));
      } else {
        return options.find((x) => x.value === value?.[0]) || null;
      }
    }, [value, options, allowMultiple]);

    return (
      <FormControl
        fullWidth
        error={error}
        className={error ? 'Mui-error' : ''}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedValue}
          options={options}
          placeholder={placeholder}
          isMulti={allowMultiple}
          isDisabled={disabled}
          classNamePrefix='react-select'
          menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              onChange(newValue.map((x) => (typeof x === 'object' && 'value' in x ? x.value : '')));
            } else if (newValue && typeof newValue === 'object' && 'value' in newValue) {
              onChange([newValue.value]);
            } else {
              onChange([]);
            }
          }}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default FieldTypePicker;
